import type { InvoiceAnalysisFilters, InvoiceAnalysisSnapshot, OrderPlanningSnapshot, PurchaseOrderItemRow } from "../../shared/types";
import { mockOrderPlanningSnapshot } from "../mocks/orderPlanning";
import { getRequired } from "./apiClient";
import { reportResponseCache } from "./reportCache";

function getWithFallback<T extends OrderPlanningSnapshot | InvoiceAnalysisSnapshot>(path: string, fallback: () => T): Promise<T> {
  // Mock data is permitted only in explicitly selected demo mode, not on a production cache miss or 429.
  if (import.meta.env.VITE_APP_MODE === "mock") return Promise.resolve(fallback());
  return reportResponseCache.get(path, () => getRequired<T>(path));
}

export interface OrderPlanningFilters { timeFrame: string; station?: string; vendor?: string; type?: string; }

function aggregateCategories(items: PurchaseOrderItemRow[]): OrderPlanningSnapshot["categories"] {
  const grouped = new Map<string, OrderPlanningSnapshot["categories"][number]>();
  items.forEach((item) => {
    const current = grouped.get(item.categoryName) || {
      categoryGroup1: "C-Store", categoryGroup2: item.categoryGroup2, categoryName: item.categoryName,
      orderQuantity: 0, orderedCost: 0, estimatedRetail: 0, salesAmount: 0, salesQuantity: 0,
      periodStartOnHandQty: 0, currentOnHandQty: 0, daysOnHand: 0
    };
    current.orderQuantity += item.orderQuantity;
    current.orderedCost += item.orderedCost;
    current.estimatedRetail += item.estimatedRetail;
    current.salesAmount += item.salesAmount;
    current.salesQuantity += item.salesQuantity;
    current.periodStartOnHandQty += item.periodStartOnHandQty;
    current.currentOnHandQty += item.currentOnHandQty;
    current.daysOnHand = Math.max(current.daysOnHand, item.daysOnHand);
    grouped.set(item.categoryName, current);
  });
  return [...grouped.values()].sort((left, right) => right.orderedCost - left.orderedCost);
}

function aggregateStations(
  orders: OrderPlanningSnapshot["orders"],
  items: PurchaseOrderItemRow[]
): OrderPlanningSnapshot["stationComparison"] {
  const grouped = new Map<string, {
    row: OrderPlanningSnapshot["stationComparison"][number];
    upcs: Set<string>;
    daysOnHand: number[];
  }>();

  orders.forEach((order) => {
    const current = grouped.get(order.stationSk) || {
      row: {
        station: order.station, stationSk: order.stationSk, orderCount: 0, itemCount: 0, orderQuantity: 0,
        orderedCost: 0, estimatedRetail: 0, salesAmount: 0, salesQuantity: 0,
        periodStartOnHandQty: 0, currentOnHandQty: 0, daysOnHand: 0
      },
      upcs: new Set<string>(),
      daysOnHand: []
    };
    current.row.orderCount += 1;
    current.row.orderQuantity += order.orderQuantity;
    current.row.orderedCost += order.orderedCost;
    current.row.estimatedRetail += order.estimatedRetail;
    grouped.set(order.stationSk, current);
  });

  items.forEach((item) => {
    const current = grouped.get(item.stationSk);
    if (!current) return;
    current.upcs.add(item.upc);
    current.row.salesAmount += item.salesAmount;
    current.row.salesQuantity += item.salesQuantity;
    current.row.periodStartOnHandQty += item.periodStartOnHandQty;
    current.row.currentOnHandQty += item.currentOnHandQty;
    if (item.daysOnHand) current.daysOnHand.push(item.daysOnHand);
  });

  return [...grouped.values()].map(({ row, upcs, daysOnHand }) => ({
    ...row,
    itemCount: upcs.size,
    daysOnHand: daysOnHand.length ? daysOnHand.reduce((sum, value) => sum + value, 0) / daysOnHand.length : 0
  })).sort((left, right) => right.orderedCost - left.orderedCost);
}

export function getOrderPlanning(filters: OrderPlanningFilters = { timeFrame: "Last7" }): Promise<OrderPlanningSnapshot> {
  const params = new URLSearchParams({ timeFrame: filters.timeFrame || "Last7" });
  if (filters.station) params.set("station", filters.station);
  if (filters.vendor) params.set("vendor", filters.vendor);
  if (filters.type) params.set("type", filters.type);
  return getWithFallback(`/reports/order-planning?${params.toString()}`, () => {
    const scopedOrders = mockOrderPlanningSnapshot.orders.filter((row) =>
      (!filters.station || row.station === filters.station)
      && (!filters.vendor || row.vendor === filters.vendor)
      && (!filters.type || row.type === filters.type)
    );
    const stationOrders = mockOrderPlanningSnapshot.orders.filter((row) => !filters.station || row.station === filters.station);
    const vendors = [...new Set(stationOrders.map((row) => row.vendor))].sort();
    const vendorOrders = stationOrders.filter((row) => !filters.vendor || row.vendor === filters.vendor);
    const types = [...new Set(vendorOrders.map((row) => row.type))].sort();
    const orderReferences = new Set(scopedOrders.map((row) => row.orderReference));
    const items = mockOrderPlanningSnapshot.items.filter((row) => orderReferences.has(row.orderReference));
    const orderedCost = scopedOrders.reduce((sum, row) => sum + row.orderedCost, 0);
    const estimatedRetail = scopedOrders.reduce((sum, row) => sum + row.estimatedRetail, 0);
    return {
      ...mockOrderPlanningSnapshot,
      filters: {
        available: { ...mockOrderPlanningSnapshot.filters.available, vendors, types },
        selected: { ...filters, timeFrame: filters.timeFrame || "Last7" }
      },
      overview: {
        ...mockOrderPlanningSnapshot.overview,
        orderCount: scopedOrders.length,
        lineCount: items.length,
        itemCount: new Set(items.map((row) => row.upc)).size,
        orderQuantity: scopedOrders.reduce((sum, row) => sum + row.orderQuantity, 0),
        orderedCost,
        estimatedRetail,
        potentialMargin: estimatedRetail - orderedCost
      },
      orders: scopedOrders,
      categories: aggregateCategories(items),
      stationComparison: aggregateStations(scopedOrders, items),
      items
    };
  });
}

export function getInvoiceAnalysis(filters: InvoiceAnalysisFilters): Promise<InvoiceAnalysisSnapshot> {
  const params = new URLSearchParams({ dateFrom: filters.dateFrom, dateTo: filters.dateTo });
  if (filters.station) params.set("station", filters.station);
  if (filters.vendor) params.set("vendor", filters.vendor);
  if (filters.categoryGroup2) params.set("categoryGroup2", filters.categoryGroup2);
  if (filters.invoiceId) params.set("invoiceId", filters.invoiceId);

  return getWithFallback(`/reports/order-planning/invoices?${params.toString()}`, () => {
    const inDateRange = (value: string) => value >= filters.dateFrom && value <= filters.dateTo;
    const datedOrders = mockOrderPlanningSnapshot.orders.filter((row) => inDateRange(row.invoicedDate));
    const stationOrders = datedOrders.filter((row) => !filters.station || row.station === filters.station);
    const vendorOrders = stationOrders.filter((row) => !filters.vendor || row.vendor === filters.vendor);
    const vendorReferences = new Set(vendorOrders.map((row) => row.orderReference));
    const categoryItems = mockOrderPlanningSnapshot.items.filter((row) => vendorReferences.has(row.orderReference));
    const scopedCategoryItems = categoryItems.filter((row) => !filters.categoryGroup2 || row.categoryGroup2 === filters.categoryGroup2);
    const availableInvoiceIds = new Set(scopedCategoryItems.map((row) => row.invoiceId));
    const orders = vendorOrders.filter((row) =>
      availableInvoiceIds.has(row.invoiceId) && (!filters.invoiceId || row.invoiceId === filters.invoiceId)
    );
    const orderReferences = new Set(orders.map((row) => row.orderReference));
    const items = scopedCategoryItems.filter((row) => orderReferences.has(row.orderReference));
    return {
      asOfDate: orders.reduce((latest, row) => row.invoicedDate > latest ? row.invoicedDate : latest, ""),
      filters: {
        available: {
          stations: [...new Set(datedOrders.map((row) => row.station))].sort(),
          vendors: [...new Set(stationOrders.map((row) => row.vendor))].sort(),
          categories: [...new Set(categoryItems.map((row) => row.categoryGroup2))].filter(Boolean).sort(),
          invoiceIds: [...availableInvoiceIds].filter(Boolean).sort((left, right) => left.localeCompare(right, undefined, { numeric: true }))
        },
        selected: filters
      },
      orders,
      items
    };
  });
}

export type { InvoiceAnalysisFilters };
